// 乗務員台帳ビュー（中身/TMS・純マスタ／登録専用）。
// 収支サマリーからは切り離した「乗務員の登録名簿」。列＝コード/氏名/担当/給与区分/固定給(月額)/状態/備考。
// drivers コレクションを直読み。＋新規登録・実データ同期（未登録の実在乗務員を取込）・状態(在籍/退職)。
// 運行数・売上・収支などの実績は載せない（「運行実績×収支」で確認）。給与区分の人件費反映は固定給額が揃い次第エンジン接続。
// デモ（Supabase未接続）時は同梱サンプルから同期プレビュー（保存は本番のみ）。
import { computeOperations, LABOR_RATE, commissionPctAt } from '../data/analytics/operations.js'
import { COLLECTIONS, listDocs, upsert } from '../data/collections.js'
import { mapDrivers, planDrivers } from '../data/ingest/masters.js'
import { decodeBytes, readTable } from '../data/ingest/csv.js'
import { loadLaborSettings, saveLaborSettings, refreshLabor } from '../data/laborRates.js'
import { normLaborTime } from '../data/laborQuote.core.js'
import { supabaseReady } from '../supabase.js'
import { loadDemoDrivers, saveDemoDriver } from '../data/driverMasters.js'
import sampleOps from './sample.ops.json'
import { allowSample } from '../data/demoData.js'

const YEN = (n) => '¥' + Math.round(Number(n) || 0).toLocaleString('ja-JP')
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const normCode = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')

const D = { el: null, ctx: {}, online: false, fetchError: false, masters: {}, rows: [], draft: null, sortKey: 'code', sortDir: 'asc', saving: '', busy: '', importPlan: null, importInactive: true, labor: { estimatePct: null }, savingPay: '' }

function styleOnce() {
  if (document.getElementById('drv-style')) return
  const s = document.createElement('style'); s.id = 'drv-style'
  s.textContent = `
  .dv{display:flex;flex-direction:column;gap:12px}
  .dv-bar{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  .dv-btn{font-family:inherit;font-size:12.5px;font-weight:700;color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:8px;padding:7px 13px;cursor:pointer}
  .dv-btn:hover{background:var(--head)}
  .dv-btn.primary{background:var(--accent-fill);color:#fff;border-color:var(--accent-fill)}
  .dv-btn.primary:hover{background:var(--accent-fill)}
  .dv-btn:disabled{opacity:.5;cursor:default}
  .dv-demo{font-size:11px;color:var(--signal);background:var(--card);border:1px solid var(--accent);border-radius:999px;padding:3px 10px}
  .dv-save{font-size:11px;color:var(--pos);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:3px 10px}
  .dv-open{margin-left:auto;font-size:12px;color:var(--text);text-decoration:none;background:var(--head);border:1px solid var(--e1);border-radius:8px;padding:6px 12px}
  .dv-kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px}
  .dv-kpi{background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:11px 13px}
  .dv-kpi .k{font-size:10.5px;color:var(--muted)}.dv-kpi .v{font-size:19px;font-weight:700;font-variant-numeric:tabular-nums;margin-top:2px}
  .dv-kpi.acc .v{color:var(--text)}
  .dv-tblwrap{border:1px solid var(--e1);border-radius:12px;overflow:hidden;background:var(--card)}
  .dv-tblwrap .cap{padding:9px 13px;font-size:12px;font-weight:700;color:var(--muted);background:var(--head);border-bottom:1px solid var(--e1);display:flex;justify-content:space-between;align-items:center}
  .dv-scroll{max-height:58vh;overflow:auto}
  table.dv-tbl{width:100%;border-collapse:collapse;font-size:12px}
  .dv-tbl th,.dv-tbl td{padding:5px 10px;border-bottom:1px solid var(--e1);text-align:left;white-space:nowrap}
  .dv-tbl th{position:sticky;top:0;background:var(--head);color:var(--text);font-size:10.5px;z-index:1;cursor:pointer;user-select:none}
  .dv-tbl th:hover{background:var(--head);color:var(--text)}
  .dv-tbl td.n,.dv-tbl th.n{text-align:right}
  .dv-tbl select,.dv-tbl input{font-family:inherit;font-size:12px;border:1px solid var(--e1);border-radius:6px;padding:3px 6px;background:var(--card);color:var(--text)}
  .dv-tbl input.num{width:100px;text-align:right;font-variant-numeric:tabular-nums}
  .dv-tbl input.num:disabled{background:var(--head);color:var(--text)}
  .dv-tbl .wage-cell{display:flex;align-items:center;gap:5px;white-space:nowrap}
  .dv-tbl .wage-cell input.num{width:86px}
  .dv-tbl .wage-cell input.pct{width:66px}
  .dv-tbl .wage-cell select{padding:3px 5px}
  .dv-tbl .wage-unit{font-size:11px;color:var(--text)}
  .dv-pay-avg{font-size:12.5px;color:var(--muted);margin-left:10px;white-space:nowrap}
  .dv-pay-avg .s{font-size:10.5px}
  .dv-pay-avg .gap{color:var(--signal);font-weight:800;margin-left:6px}
  .dv-tbl .rate-from{font-family:inherit;font-size:10.5px;border:1px solid var(--e1);background:var(--card);color:var(--text);padding:2px 4px;width:118px}
  .dv-rhist{font-size:10px;color:var(--muted);margin-top:3px;white-space:nowrap}
  .dv-rhist .rh-d{color:var(--muted)}
  .dv-rhist .rh-x{font-family:inherit;font-size:10px;color:var(--text);background:transparent;border:1px solid var(--e1);border-radius:0;padding:0 4px;margin-left:2px;cursor:pointer}
  .dv-rhist .rh-x:hover{border-color:var(--accent)}
  .dv-tbl .wage-lead{font-size:11.5px;color:var(--text);font-weight:600}
  .dv-tbl th.wagecol,.dv-tbl td.wagecol{cursor:default}
  .dv-pay{display:flex;align-items:center;gap:12px;flex-wrap:wrap;background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:8px 12px;font-size:12.5px;color:var(--text)}
  .dv-pay-title{font-weight:800;font-size:13px}
  .dv-pay-rate{font-weight:700}
  .dv-pay-rate input.num{width:70px;text-align:right;font-family:inherit;font-size:12px;border:1px solid var(--e1);border-radius:6px;padding:3px 6px}
  .dv-pay-hint{font-size:11px;color:var(--muted)}
  .dv-tbl input.w-name{width:120px}.dv-tbl input.w-cd{width:70px}.dv-tbl input.w-staff{width:80px}.dv-tbl input.w-memo{width:150px}
  .dv-tbl tr.off td{background:var(--card)}.dv-tbl tr.off input,.dv-tbl tr.off select{color:var(--text)}
  .dv-tbl tr.draft td{background:var(--card)}
  .dv-cd{font-variant-numeric:tabular-nums;font-weight:700}
  .dv-note{font-size:11px;color:var(--muted);line-height:1.6}
  .dv-modal{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:50;padding:20px}
  .dv-card{background:var(--card);border-radius:14px;width:min(720px,96vw);max-height:88vh;display:flex;flex-direction:column;border:1px solid var(--e1);box-shadow:inset 0 1px 0 var(--hilite);overflow:hidden}
  .dv-card-h{padding:13px 16px;font-size:14px;font-weight:800;color:var(--text);border-bottom:1px solid var(--e1);display:flex;gap:10px;align-items:baseline}
  .dv-card-sum{padding:11px 16px;font-size:12.5px;color:var(--text);background:var(--card);border-bottom:1px solid var(--e1);line-height:1.65}
  .dv-card-scroll{overflow:auto;padding:2px 8px}
  .dv-card-f{padding:12px 16px;display:flex;gap:10px;justify-content:flex-end;border-top:1px solid var(--e1)}
  .dv-chk{display:inline-flex;align-items:center;gap:6px;margin-top:7px;font-size:12px;color:var(--text);font-weight:700;cursor:pointer}
  .dv-bnew{color:var(--pos);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700}
  .dv-benr{color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700}
  .dv-bskip{color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 8px;font-size:11px}
  `
  document.head.appendChild(s)
}

function buildRows() {
  D.rows = Object.values(D.masters).map((m) => ({
    id: m.id, code: m.code || m.id, name: m.name || '', staff: m.staff || '',
    wageType: m.wageType || 'commission',
    fixedKind: m.fixedKind || 'monthly',
    monthlySalary: (m.monthlySalary != null ? m.monthlySalary : ''),
    dailyWage: (m.dailyWage != null ? m.dailyWage : ''),
    // 表示は「今日時点の有効率」（適用開始日つき解決 #5）。未設定は試算用率（初期値35%）。
    commissionRate: (() => { const pct = commissionPctAt(m, todayISO()); if (pct != null) return pct; const est = D.labor.estimatePct; return (est != null && est !== '') ? Number(est) : (LABOR_RATE * 100) })(),
    rates: Array.isArray(m.rates) ? m.rates : [],
    status: m.status || 'active', note: m.note || '',
  }))
}

const COLS = [['code', 'コード'], ['name', '氏名'], ['staff', '担当'], ['wageType', '給与区分'], ['wage', '給与設定'], ['status', '状態'], ['note', '備考']]

function sortRows(rows) {
  const k = D.sortKey, mul = D.sortDir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    let x = a[k], y = b[k]
    if (k === 'monthlySalary') return ((Number(x) || 0) - (Number(y) || 0)) * mul
    if (k === 'code') { const nx = Number(x), ny = Number(y); if (!isNaN(nx) && !isNaN(ny)) return (nx - ny) * mul }
    return String(x).localeCompare(String(y), 'ja') * mul
  })
}

const todayISO = () => { const d = new Date(); const p2 = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}` }

// 歩合率の改定履歴（日付つきエントリがある場合のみ表示）。×で改定を取り消し。
function rateHist(r) {
  const dated = (r.rates || []).filter((e) => e && e.from).sort((a, b) => String(a.from).localeCompare(String(b.from)))
  if (!dated.length) return ''
  const base = (r.rates || []).find((e) => e && !e.from)
  const today = todayISO()
  const parts = []
  if (base) parts.push(`当初 ${esc(base.pct)}%`)
  for (const e of dated) parts.push(`→ ${esc(e.pct)}% <span class="rh-d">(${esc(e.from)}〜${e.from > today ? '・予定' : ''})</span><button class="rh-x" data-rdel="${esc(e.from)}" data-rid="${esc(r.id)}" title="この改定を取り消す">×</button>`)
  return `<div class="dv-rhist">${parts.join(' ')}</div>`
}

function rowInputs(r, draft) {
  const idAttr = draft ? 'data-draft="1"' : `data-id="${esc(r.id)}"`
  const cd = draft ? `<input class="w-cd" data-f="code" value="${esc(r.code)}" placeholder="コード" ${idAttr}>` : `<span class="dv-cd">${esc(r.code)}</span>`
  const fixed = r.wageType === 'fixed'
  const daily = r.fixedKind === 'daily'
  const defPct = (LABOR_RATE * 100).toFixed(2)
  const wageCell = fixed
    ? `<div class="wage-cell">
        <select data-f="fixedKind" ${idAttr}><option value="monthly"${!daily ? ' selected' : ''}>月給</option><option value="daily"${daily ? ' selected' : ''}>日給</option></select>
        ${daily
          ? `<input class="num" type="number" step="1000" min="0" data-f="dailyWage" value="${esc(r.dailyWage)}" placeholder="日額" ${idAttr}><span class="wage-unit">円/日</span>`
          : `<input class="num" type="number" step="10000" min="0" data-f="monthlySalary" value="${esc(r.monthlySalary)}" placeholder="月額" ${idAttr}><span class="wage-unit">円/月</span>`}
      </div>`
    : `<div class="wage-cell">
        <span class="wage-lead">運賃 ×</span>
        <input class="num pct" type="number" step="0.1" min="0" max="100" data-f="commissionRate" value="${esc(r.commissionRate)}" placeholder="${defPct}" ${idAttr}><span class="wage-unit">%</span>
        ${draft ? '' : `<input class="rate-from" type="date" data-rf="${esc(r.id)}" title="適用開始日（空欄=当初率の修正。日付を入れて率を変えると、その日からの改定として履歴に積まれます）">`}
      </div>${rateHist(r)}`
  return `
    <td>${cd}</td>
    <td><input class="w-name" data-f="name" value="${esc(r.name)}" ${idAttr}></td>
    <td><input class="w-staff" data-f="staff" value="${esc(r.staff)}" ${idAttr}></td>
    <td><select data-f="wageType" ${idAttr}><option value="commission"${!fixed ? ' selected' : ''}>歩合</option><option value="fixed"${fixed ? ' selected' : ''}>固定給</option></select></td>
    <td class="wagecol">${wageCell}</td>
    <td><select data-f="status" ${idAttr}><option value="active"${r.status !== 'inactive' ? ' selected' : ''}>在籍</option><option value="inactive"${r.status === 'inactive' ? ' selected' : ''}>退職</option></select></td>
    <td><input class="w-memo" data-f="note" value="${esc(r.note)}" ${idAttr}></td>`
}

function paint() {
  const el = D.el
  const rows = sortRows(D.rows)
  const nFixed = D.rows.filter((r) => r.wageType === 'fixed').length
  const active = D.rows.filter((r) => r.status !== 'inactive').length
  const defRatePct = (D.labor.estimatePct != null && D.labor.estimatePct !== '') ? Number(D.labor.estimatePct) : (LABOR_RATE * 100)
  const lt = normLaborTime(D.labor) // 時間ベース試算の時給・係数（未設定は既定を表示＝estimatePct と同じ流儀）
  // 実効平均（#4）: 在籍×歩合の乗務員の「今日時点の有効率」の単純平均。参考表示（自動計算・編集不可）。
  // 試算用率と1pt以上乖離したら注意表示＝試算用の暫定変更の戻し忘れ検知。
  const commRates = Object.values(D.masters)
    .filter((m) => (m.status || 'active') !== 'inactive' && (m.wageType || 'commission') === 'commission')
    .map((m) => { const pct = commissionPctAt(m, todayISO()); return pct != null ? pct : defRatePct })
  const avgPct = commRates.length ? commRates.reduce((x, y) => x + y, 0) / commRates.length : null
  const gapPt = avgPct != null ? Math.abs(avgPct - defRatePct) : 0
  const arrow = (k) => D.sortKey === k ? (D.sortDir === 'asc' ? ' ▲' : ' ▼') : ''
  const head = COLS.map((c) => {
    const sortable = c[0] !== 'wage'
    return `<th class="${c[0] === 'wage' ? 'wagecol' : ''}"${sortable ? ` data-k="${c[0]}"` : ''}>${c[1]}${sortable ? arrow(c[0]) : ''}</th>`
  }).join('') + '<th></th>'
  const draftRow = D.draft ? `<tr class="draft">${rowInputs(D.draft, true)}<td><button class="dv-btn primary" id="dv-commit">登録</button></td></tr>` : ''
  const body = rows.map((r) => `<tr class="${r.status === 'inactive' ? 'off' : ''}">${rowInputs(r, false)}<td></td></tr>`).join('')
  const empty = (!rows.length && !D.draft)
    ? '<div class="ct-state is-empty"><b class="ct-state__t">まだ乗務員が登録されていません</b><span>「実データから取込」で運行データに出てくる実在乗務員を一括登録できます。／「＋新規乗務員」で1名ずつ追加もできます。</span></div>'
    : ''
  el.innerHTML = `
  <section class="dv">
    <div class="dv-pay">
      <b class="dv-pay-title">人件費設定</b>
      <span class="dv-pay-rate">時給(円/h) 中型 <input type="number" id="dv-hr-med" class="num" step="10" min="0" value="${esc(lt.hourlyMedium)}" ${D.online ? '' : 'disabled'}> 大型 <input type="number" id="dv-hr-lg" class="num" step="10" min="0" value="${esc(lt.hourlyLarge)}" ${D.online ? '' : 'disabled'}></span>
      <span class="dv-pay-rate">拘束＝走行時間×<input type="number" id="dv-t-fac" class="num" step="0.05" min="0" value="${esc(lt.driveFactor)}" ${D.online ? '' : 'disabled'}>＋積卸 <input type="number" id="dv-t-buf" class="num" step="5" min="0" value="${esc(lt.stopBufferMin)}" ${D.online ? '' : 'disabled'}> 分/地点</span>
      <span class="dv-pay-rate">試算用 人件費率 <input type="number" id="dv-est-rate" class="num" step="0.1" min="0" max="100" value="${esc(defRatePct)}" ${D.online ? '' : 'disabled'}> %</span>
      <span class="dv-pay-hint">受注試算は所要時間×時給（8h超×1.25・日祝着/深夜+20%）。率は時間未取得時のフォールバックと、個別設定が無い乗務員の収支に使用</span>
      ${avgPct != null ? `<span class="dv-pay-avg">実効平均 ${(Math.round(avgPct * 10) / 10)}%<span class="s">（在籍・歩合 ${commRates.length}名の単純平均）</span>${gapPt >= 1 ? `<b class="gap">⚠ 試算用と ${(Math.round(gapPt * 10) / 10)}pt 乖離</b>` : ''}</span>` : ''}
      <button class="dv-btn" id="dv-est-save" ${D.online ? '' : 'disabled'}>保存</button>
      ${D.savingPay ? `<span class="dv-save">${esc(D.savingPay)}</span>` : ''}
      ${D.online ? '' : '<span class="dv-demo">保存は本番のみ</span>'}
    </div>
    <div class="dv-bar">
      <button class="dv-btn primary" id="dv-add">＋新規乗務員</button>
      <button class="dv-btn" id="dv-sync" ${D.busy ? 'disabled' : ''}>${D.busy || '実データから取込'}</button>
      <button class="dv-btn" id="dv-import" ${D.busy ? 'disabled' : ''}>CSVから取込</button>
      <input type="file" id="dv-csv" accept=".csv,.txt" style="display:none">
      ${D.online ? (D.saving ? `<span class="dv-save">${esc(D.saving)}</span>` : '') : '<span class="dv-demo">デモ表示 · この端末にだけ保存（Supabase未設定）</span>'}
      <a class="dv-open" href="#/operations">運行実績×収支へ →</a>
    </div>
    <div class="dv-kpis">
      <div class="dv-kpi acc"><div class="k">登録乗務員</div><div class="v">${D.rows.length}名</div></div>
      <div class="dv-kpi"><div class="k">在籍 / 退職</div><div class="v">${active} / ${D.rows.length - active}</div></div>
      <div class="dv-kpi"><div class="k">歩合 / 固定給</div><div class="v">${D.rows.length - nFixed} / ${nFixed}</div></div>
      <div class="dv-kpi"><div class="k">試算用 率</div><div class="v">${defRatePct.toFixed(defRatePct % 1 ? 2 : 0)}%</div></div>
    </div>
    <div class="dv-tblwrap">
      <div class="cap"><span>乗務員台帳（登録名簿・${D.rows.length}名）</span><span class="dv-note">各自の歩合率（適用開始日つき）が収支の人件費率。固定給の反映式は今後。</span></div>
      ${empty || `<div class="dv-scroll"><table class="dv-tbl"><thead><tr>${head}</tr></thead><tbody>${draftRow}${body}</tbody></table></div>`}
    </div>
    <div class="dv-note">
      この台帳は「乗務員の登録簿」です（収支サマリーとは独立）。運行数・売上・収支は「運行実績×収支」で確認します。
給与区分＝<b>歩合</b>は「運賃×歩合率（％）」を個別に入力。入力した率は<b>そのまま収支の人件費率として常に使われます</b>（適用開始日つき＝改定しても過去の月の収支は変わりません）。未入力・<b>固定給</b>の乗務員は、収支では最上部の<b>「試算用」率</b>で計算します。<b>月給</b>は「給与・会計」画面の支給予定額に月額がそのまま出ます。<b>日給</b>は同じ画面で<b>支給対象日数</b>を入れると「日額 × 日数」で支給予定額が出ます（既定値は<b>暦日ベースの稼働日</b>＝土日祝も 1 日として数えます）。退職者は<b>状態＝退職</b>に（物理削除はしません）。
    </div>
  ${D.importPlan ? renderImport() : ''}
  </section>`

  el.querySelector('#dv-add').addEventListener('click', () => {
    if (!D.draft) D.draft = { code: '', name: '', staff: '', wageType: 'commission', fixedKind: 'monthly', monthlySalary: '', dailyWage: '', commissionRate: (LABOR_RATE * 100), status: 'active', note: '' }
    paint()
  })
  el.querySelector('#dv-sync').addEventListener('click', syncFromData)
  const impBtn = el.querySelector('#dv-import'), csvInp = el.querySelector('#dv-csv')
  if (impBtn && csvInp) {
    impBtn.addEventListener('click', () => csvInp.click())
    csvInp.addEventListener('change', (e) => { const f = e.target.files && e.target.files[0]; if (f) onCsvFile(f); e.target.value = '' })
  }
  const impOk = el.querySelector('#dv-imp-ok'); if (impOk) impOk.addEventListener('click', applyImport)
  const impCancel = el.querySelector('#dv-imp-cancel'); if (impCancel) impCancel.addEventListener('click', () => { D.importPlan = null; paint() })
  const impInact = el.querySelector('#dv-imp-inact'); if (impInact) impInact.addEventListener('change', (e) => { D.importInactive = e.target.checked; paint() })
  const commit = el.querySelector('#dv-commit')
  if (commit) commit.addEventListener('click', commitDraft)
  const estRate = el.querySelector('#dv-est-rate')
  if (estRate) estRate.addEventListener('input', () => { D.labor.estimatePct = estRate.value === '' ? '' : Number(estRate.value) })
  // 時間ベース試算の新フィールド（保存は同じ「保存」ボタン。未設定・不正値は読む側の normLaborTime が既定へ倒す）
  for (const [id, key] of [['dv-hr-med', 'hourlyMedium'], ['dv-hr-lg', 'hourlyLarge'], ['dv-t-fac', 'driveFactor'], ['dv-t-buf', 'stopBufferMin']]) {
    const inp = el.querySelector('#' + id)
    if (inp) inp.addEventListener('input', () => { D.labor[key] = inp.value === '' ? null : Number(inp.value) })
  }
  const estSave = el.querySelector('#dv-est-save')
  if (estSave) estSave.addEventListener('click', saveLabor)
  el.querySelectorAll('.dv-tbl th').forEach((th) => th.addEventListener('click', () => {
    const k = th.dataset.k; if (!k) return
    if (D.sortKey === k) D.sortDir = D.sortDir === 'asc' ? 'desc' : 'asc'; else { D.sortKey = k; D.sortDir = 'asc' }
    paint()
  }))
  const NUMF = new Set(['monthlySalary', 'dailyWage', 'commissionRate'])
  el.querySelectorAll('[data-id]').forEach((inp) => inp.addEventListener('change', () => {
    const id = inp.dataset.id, f = inp.dataset.f
    let v = inp.value; if (NUMF.has(f)) v = v === '' ? '' : Number(v)
    if (f === 'commissionRate') { applyRateChange(id, v); return } // 適用開始日つき保存（#5）
    saveMaster(id, { [f]: v }) // wageType/fixedKind変更時は saveMaster→paint で給与設定欄を切替
  }))
  // 改定の取り消し（履歴の×）
  el.querySelectorAll('[data-rdel]').forEach((b) => b.addEventListener('click', () => {
    const id = b.dataset.rid, from = b.dataset.rdel
    const m = D.masters[id]; if (!m) return
    const rates = (Array.isArray(m.rates) ? m.rates : []).filter((e) => !(e && String(e.from || '') === from))
    saveMaster(id, { rates, commissionRate: effScalar(rates, m) })
  }))
  el.querySelectorAll('[data-draft]').forEach((inp) => inp.addEventListener('input', () => {
    const f = inp.dataset.f; let v = inp.value; if (NUMF.has(f)) v = v === '' ? '' : Number(v)
    if (D.draft) D.draft[f] = v
    if (f === 'wageType' || f === 'fixedKind') paint() // 給与設定欄の表示を切替
  }))
}

// 「試算用」人件費率（settings/labor.estimatePct）を保存 → 収支エンジンへ即反映。
async function saveLabor() {
  if (!D.online) return
  D.savingPay = '保存中…'; paint()
  try {
    const pct = (D.labor.estimatePct === '' || D.labor.estimatePct == null) ? null : Number(D.labor.estimatePct)
    const numOrNull = (v) => (v === '' || v == null || isNaN(Number(v))) ? null : Number(v)
    await saveLaborSettings({
      estimatePct: pct,
      hourlyMedium: numOrNull(D.labor.hourlyMedium), hourlyLarge: numOrNull(D.labor.hourlyLarge),
      driveFactor: numOrNull(D.labor.driveFactor), stopBufferMin: numOrNull(D.labor.stopBufferMin),
    })
    await refreshLabor(true)
    D.savingPay = '保存しました ✓（受注試算・フォールバックに反映）'
  } catch (e) { D.savingPay = '保存失敗（権限/接続）' }
  paint(); setTimeout(() => { D.savingPay = ''; paint() }, 2400)
}

// スカラー commissionRate は「今日時点の有効率」のミラー（後方互換）。履歴が真実。
function effScalar(rates, m) {
  const pct = commissionPctAt({ wageType: 'commission', rates }, todayISO())
  if (pct != null) return pct
  return (m && m.commissionRate != null && m.commissionRate !== '') ? m.commissionRate : null
}

// 歩合率の変更（#5）。行の適用開始日input が
//  - 空欄: 当初率(from:'')の修正＝過去も含め全期間に効く（従来どおりの直し）
//  - 日付あり: その日からの改定として履歴へ upsert（同じ日付は上書き＝入力中の連打で増殖しない）
function applyRateChange(id, v) {
  const m = D.masters[id] || {}
  const from = (D.el.querySelector(`[data-rf="${CSS.escape(id)}"]`) || {}).value || ''
  let rates = Array.isArray(m.rates) ? [...m.rates] : []
  if (v === '' || v == null || isNaN(Number(v))) return
  if (from) {
    // 当初率が未材化なら、現スカラーを当初として先に固定（過去期間の率を変えないため）
    if (!rates.some((e) => e && !e.from)) {
      const sc = m.commissionRate
      if (sc != null && sc !== '' && !isNaN(Number(sc))) rates.push({ from: '', pct: Number(sc) })
    }
    rates = rates.filter((e) => !(e && String(e.from || '') === from))
    rates.push({ from, pct: Number(v) })
  } else {
    rates = rates.filter((e) => !(e && !e.from))
    rates.push({ from: '', pct: Number(v) })
  }
  saveMaster(id, { rates, commissionRate: effScalar(rates, m) })
}

async function saveMaster(id, patch) {
  const cur = D.masters[id] || { id }
  D.masters[id] = { ...cur, ...patch }
  buildRows()
  if (D.online) {
    try {
      D.saving = '保存中…'; paint()
      const m = D.masters[id]
      const numOrNull = (x) => (x === '' || x == null) ? null : Number(x)
      await upsert(COLLECTIONS.drivers, id, { code: m.code || id, name: m.name || '', staff: m.staff || '', wageType: m.wageType || 'commission', fixedKind: m.fixedKind || 'monthly', monthlySalary: numOrNull(m.monthlySalary), dailyWage: numOrNull(m.dailyWage), commissionRate: numOrNull(m.commissionRate), rates: Array.isArray(m.rates) ? m.rates : [], status: m.status || 'active', note: m.note || '', updatedAt: new Date().toISOString() })
      D.saving = '保存しました ✓'
    } catch (e) { D.saving = '保存失敗（権限/接続）' }
    paint(); if (D.saving) setTimeout(() => { D.saving = ''; paint() }, 1600)
  } else {
    // デモ（Supabase未設定）: ブラウザローカルの保管庫へ残す。画面を移っても消えず、
    // 給与（#/payroll）・収支が同じマスタを読む。本番の保存経路（上の upsert）は不変。
    saveDemoDriver(id, D.masters[id])
    paint()
  }
}

async function commitDraft() {
  const d = D.draft; if (!d) return
  const id = normCode(d.code)
  if (!id) { D.saving = 'コードを入力してください'; paint(); setTimeout(() => { D.saving = ''; paint() }, 1600); return }
  if (D.masters[id]) { D.saving = 'そのコードは登録済みです'; paint(); setTimeout(() => { D.saving = ''; paint() }, 1800); return }
  D.draft = null
  await saveMaster(id, { code: id, name: d.name, staff: d.staff, wageType: d.wageType, fixedKind: d.fixedKind || 'monthly', monthlySalary: d.monthlySalary, dailyWage: d.dailyWage, commissionRate: d.commissionRate, status: d.status, note: d.note })
}

// 実データに実在する乗務員のうち、未登録のものを台帳へ一括登録（非破壊）。
async function syncFromData() {
  D.busy = '取込中…'; paint()
  try {
    let trips = sampleOps.trips, tachos = sampleOps.tachographs
    if (D.online) {
      const [tr, ta] = await Promise.all([listDocs(COLLECTIONS.trips), listDocs(COLLECTIONS.tachographs)])
      if (tr && tr.length) { trips = tr; tachos = ta || [] }
      // 実データ0件のときサンプルを取り込むと、架空の車番・乗務員が実DBへ入る。
      // サンプルを使ってよいかは共通判定 allowSample（設定 settings/display）に委ねる（2026-08-27 案C）
      else if (!allowSample(D.online)) { trips = []; tachos = [] }
    }
    const names = {}; for (const m of Object.values(D.masters)) if (m.name) names[m.id] = m.name
    const r = computeOperations(trips, tachos, { names })
    // 担当（trips.staff）を乗務員コードごとの最多で
    const staffCnt = {}
    for (const t of trips) { if (t.kind !== 'own') continue; const c = normCode(t.driverCode); if (!c) continue; const s = t.staff || ''; (staffCnt[c] || (staffCnt[c] = {}))[s] = (staffCnt[c][s] || 0) + 1 }
    const domStaff = (c) => { const m = staffCnt[c]; if (!m) return ''; return Object.entries(m).sort((a, b) => b[1] - a[1])[0][0] }
    const cand = {}
    for (const dr of r.drivers) if (dr.code) cand[dr.code] = dr.name || dr.code
    for (const t of tachos) { const c = normCode(t.driverCode); if (c && !cand[c]) cand[c] = t.driverName || c }
    let added = 0, enriched = 0
    for (const c in cand) {
      const ex = D.masters[c]
      if (ex) {
        // 非破壊エンリッチ：氏名/担当が空なら実データで補完（既存の編集は上書きしない）
        const patch = {}
        if ((!ex.name || ex.name === '') && cand[c] && cand[c] !== c) patch.name = cand[c]
        if ((!ex.staff || ex.staff === '') && domStaff(c)) patch.staff = domStaff(c)
        if (Object.keys(patch).length) {
          D.masters[c] = { ...ex, ...patch }
          if (D.online) await upsert(COLLECTIONS.drivers, c, { ...patch, updatedAt: new Date().toISOString() })
          else saveDemoDriver(c, D.masters[c])
          enriched++
        }
        continue
      }
      const rec = { id: c, code: c, name: cand[c], staff: domStaff(c), wageType: 'commission', monthlySalary: '', status: 'active', note: '' }
      D.masters[c] = rec
      if (D.online) await upsert(COLLECTIONS.drivers, c, { code: c, name: cand[c], staff: domStaff(c), wageType: 'commission', status: 'active', updatedAt: new Date().toISOString() })
      else saveDemoDriver(c, rec) // デモはブラウザローカルへ
      added++
    }
    buildRows(); D.busy = ''; D.saving = `新規${added}名・情報補完${enriched}名 ✓`; paint()
    setTimeout(() => { D.saving = ''; paint() }, 2200)
  } catch (e) { D.busy = ''; D.saving = '取込に失敗しました'; paint(); setTimeout(() => { D.saving = ''; paint() }, 2200) }
}

// CSVから取込：ファイル → 文字コード自動復号 → 乗務員コードでマッピング → 既存照合 → プレビュー。
async function onCsvFile(file) {
  D.busy = '読込中…'; paint()
  try {
    const buf = await file.arrayBuffer()
    const text = decodeBytes(new Uint8Array(buf), 'auto')
    const { records } = readTable(text)
    const cands = mapDrivers(records)
    if (!cands.length) throw new Error('「乗務員コード」列が見つかりませんでした')
    const planned = planDrivers(cands, D.masters)
    const counts = { total: planned.length, new: 0, enrich: 0, skip: 0, newActive: 0, newInactive: 0 }
    for (const p of planned) { counts[p.kind]++; if (p.kind === 'new') { if (p.status === 'inactive') counts.newInactive++; else counts.newActive++ } }
    D.importPlan = { fileName: file.name, planned, counts }
    D.busy = ''; paint()
  } catch (e) { D.busy = ''; D.saving = 'CSV読込に失敗: ' + (e && e.message ? e.message : e); paint(); setTimeout(() => { D.saving = ''; paint() }, 2800) }
}

function renderImport() {
  const p = D.importPlan
  const badge = (k) => k === 'new' ? '<span class="dv-bnew">新規</span>' : k === 'enrich' ? '<span class="dv-benr">補完</span>' : '<span class="dv-bskip">変更なし</span>'
  const body = p.planned.map((r) => `<tr class="${r.status === 'inactive' ? 'off' : ''}"><td class="dv-cd">${esc(r.code)}</td><td>${esc(r.name || '—')}</td><td>${esc(r.depot || '')}</td><td>${r.status === 'inactive' ? '退職' : '在籍'}</td><td>${badge(r.kind)}</td></tr>`).join('')
  const applyNew = D.importInactive ? p.counts.new : p.counts.newActive
  return `
  <div class="dv-modal" id="dv-modal">
    <div class="dv-card">
      <div class="dv-card-h">CSVから取込プレビュー<span class="dv-note">${esc(p.fileName)}</span></div>
      <div class="dv-card-sum">
        全${p.counts.total}件 ／ <b>新規 ${p.counts.new}名</b>（在籍${p.counts.newActive}・退職${p.counts.newInactive}）・情報補完 ${p.counts.enrich}名・変更なし ${p.counts.skip}名
        <label class="dv-chk"><input type="checkbox" id="dv-imp-inact" ${D.importInactive ? 'checked' : ''}> 無所属＝退職扱い（${p.counts.newInactive}名）も取り込む</label>
        <div class="dv-note">コード＝CSVの「乗務員コード」。給与区分・固定給はCSVに無いため取込対象外（新規は歩合で登録）。所属が無い＝無所属は「退職」で登録します。既存者は空欄の氏名のみ補完、給与区分・状態・担当は上書きしません。</div>
      </div>
      <div class="dv-card-scroll"><table class="dv-tbl"><thead><tr><th>コード</th><th>氏名</th><th>所属</th><th>状態</th><th>判定</th></tr></thead><tbody>${body}</tbody></table></div>
      <div class="dv-card-f">
        <button class="dv-btn" id="dv-imp-cancel">キャンセル</button>
        <button class="dv-btn primary" id="dv-imp-ok">この内容で取込（新規${applyNew}・補完${p.counts.enrich}）</button>
      </div>
    </div>
  </div>`
}

async function applyImport() {
  const p = D.importPlan; if (!p) return
  const inclInactive = D.importInactive
  D.importPlan = null; D.busy = '取込中…'; paint()
  let added = 0, enriched = 0
  try {
    for (const r of p.planned) {
      if (r.kind === 'new') {
        if (r.status === 'inactive' && !inclInactive) continue
        const rec = { id: r.id, code: r.code, name: r.name || '', kana: r.kana || '', staff: '', wageType: 'commission', monthlySalary: '', status: r.status, note: r.depot || '', depot: r.depot || '' }
        D.masters[r.id] = rec
        if (D.online) await upsert(COLLECTIONS.drivers, r.id, { code: rec.code, name: rec.name, kana: rec.kana, staff: '', wageType: 'commission', status: rec.status, note: rec.note, depot: rec.depot, updatedAt: new Date().toISOString() })
        else saveDemoDriver(r.id, rec)
        added++
      } else if (r.kind === 'enrich') {
        const ex = D.masters[r.id]; if (!ex) continue
        D.masters[r.id] = { ...ex, ...r.patch }
        if (D.online) await upsert(COLLECTIONS.drivers, r.id, { ...r.patch, updatedAt: new Date().toISOString() })
        else saveDemoDriver(r.id, D.masters[r.id])
        enriched++
      }
    }
    buildRows(); D.busy = ''; D.saving = `CSV取込: 新規${added}名・補完${enriched}名 ✓`; paint()
    setTimeout(() => { D.saving = ''; paint() }, 2800)
  } catch (e) { D.busy = ''; D.saving = '取込に失敗しました（権限/接続）'; paint(); setTimeout(() => { D.saving = ''; paint() }, 2800) }
}

export async function renderDrivers(el, ctx = {}) {
  styleOnce()
  D.el = el
  D.ctx = ctx
  D.online = !ctx.demo && supabaseReady
  D.fetchError = false
  D.masters = {}; D.draft = null; D.savingPay = ''
  D.labor = { estimatePct: null }
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">読み込んでいます…</b><span>乗務員台帳を読み込んでいます</span></div>'
  if (D.online) {
    try { const dr = await listDocs(COLLECTIONS.drivers); for (const m of (dr || [])) { const id = normCode(m.code || m.id || ''); if (id) D.masters[id] = { ...m, id } } } catch { D.online = false; D.fetchError = true }
    try { D.labor = await loadLaborSettings() } catch { /* 初期値35%のまま */ }
  } else {
    // デモ（Supabase未設定）: ブラウザローカルの保管庫を台帳として読む（給与・収支と同じマスタ）
    D.masters = loadDemoDrivers()
  }
  // サインイン済みの実fetch失敗時は「0名の空状態」を装わず is-error を主表示にする
  // （デモ表示は明示的なデモ状況＝online初期値false のときだけ。2026-08-27 負債解消）
  if (D.fetchError) { paintFetchError(); return }
  buildRows()
  paint()
}

// 実fetch失敗（デモ意図とは別）の主表示。空の台帳を装わない
function paintFetchError() {
  D.el.innerHTML = '<section class="dv"><div class="ct-state is-error"><b class="ct-state__t">乗務員台帳を読み込めませんでした</b><span>保存済みの台帳データを取得できていません。接続を確認して再試行してください</span><button type="button" class="btn" id="dv-retry">再試行</button></div></section>'
  const retry = D.el.querySelector('#dv-retry'); if (retry) retry.addEventListener('click', () => { renderDrivers(D.el, D.ctx) })
}
